'use client';

import { Flex, Box, Grid, GridItem, Text } from '@chakra-ui/react';
import Filters from '@/components/Product/Filters';
import SortBy from '@/components/Product/SortBy';
import ProductItem from '@/components/Product/ProductItem/ProductItem';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IFilterObject } from '@/components/Product/Filters/types';
import { ISortOption } from '@/components/Product/SortBy/types';
import { SortOption } from '@/components/Product/SortBy/constants';
import {
  ANY_VALUE_RANGE,
  PRODUCT_VARIANT_REFRESH_INTERVAL,
} from '@/constants/product';
import { mapAppProductVariantToProductItem } from '@/utils/product';
import {
  IAppProductVariant,
  Metal,
  StockAvailability,
} from '@/types/api/app/product';
import {
  useGetProductVariantsCountQuery,
  useGetProductVariantsInfiniteQuery,
} from '@/store/apis/app.product-variants';
import { IAppCategory } from '@/types/api/app/category';
import { flatten, isEqual, map, uniq } from 'lodash';
import { Where, WhereBuilder } from '@loopback/filter';
import { IProductMetalType } from '@/types/product';
import ProductListEmptyQueryState from '@/components/Product/ProductListEmptyQueryState';
import { useGetInStockProductIdsQuery } from '@/store/apis/app.products';
import ReduxProviders from '@/providers/ReduxProvider';
import ProductGridActionsSkeleton from '@/components/Skeleton/ProductGridActionsSkeleton';
import ProductGridSkeleton from '@/components/Skeleton/ProductGridSkeleton';
import colors from '@/constants/theme/colors';

interface ShopItemListProps {
  ssrProductVariants?: IAppProductVariant[];
  isDeviceMobile?: boolean;
  productIds?: number[];
  variantIds?: number[];
  disabledFiltering?: Array<'value' | 'metal' | 'category'>;
  parentPath?: string;
}
const ShopItemListBase: FC<ShopItemListProps> = ({
  productIds,
  variantIds = [],
  ssrProductVariants = [],
  isDeviceMobile = false,
  disabledFiltering,
  parentPath,
}) => {
  const perPage = isDeviceMobile ? 8 : 16;
  const [currPage, setCurrPage] = useState<number>(1);
  const [filters, setFilters] = useState<IFilterObject[]>([]);
  const [categories, setCategories] = useState<IAppCategory[]>([]);
  const [valueRange, setValueRange] = useState<number[]>(ANY_VALUE_RANGE);
  const [sortBy, setSortBy] = useState<ISortOption>(SortOption.latest);

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const productIdsFromCategories = uniq(
    flatten(map(categories, ({ productIds }) => productIds))
  );

  const { data: inStockProductsIds = [] } = useGetInStockProductIdsQuery();

  const productVariantsWhere = useMemo(() => {
    // get "where" for productVariants
    const productVariantsWhereBuilder = new WhereBuilder<IAppProductVariant>();
    const andWhere: Where<IAppProductVariant>[] = [
      {
        stockAvailability: { neq: StockAvailability.Deactivated },
      },
    ];

    let inStockProductIds = productIds ?? inStockProductsIds;
    if (!!categories.length) {
      inStockProductIds = inStockProductIds.filter((id) =>
        productIdsFromCategories.includes(id)
      );
    }

    const metalFilters = filters.filter(({ key }) => key === 'metal');
    if (metalFilters.length) {
      let inqValues: Metal[] = [];
      metalFilters.forEach(({ value }) => {
        switch (value) {
          case IProductMetalType.platinum:
            inqValues.push(Metal.Platinum);
            break;
          case IProductMetalType.gold:
            inqValues.push(Metal.YellowGold18K, Metal.YellowGold24K);
            break;
          case IProductMetalType.whiteGold:
            inqValues.push(Metal.WhiteGold18K);
            break;
        }
      });
      andWhere.push({ metal: { inq: inqValues } });
    }

    if (!isEqual(valueRange, ANY_VALUE_RANGE)) {
      // price is store by USD-unit * 100
      andWhere.push({
        price: { between: [valueRange[0] * 100, valueRange[1] * 100] },
      });
    }

    productVariantsWhereBuilder.and(andWhere);
    productVariantsWhereBuilder.or([
      {
        productId: { inq: inStockProductIds },
      },
      { id: { inq: variantIds } },
    ]);
    return productVariantsWhereBuilder.build();
  }, [
    filters,
    categories,
    valueRange,
    productIdsFromCategories,
    productIds,
    inStockProductsIds,
    variantIds,
  ]);

  const {
    data: productVariantsCount = 0,
    isLoading: isGetProductVariantsCountLoading,
    isFetching: isGetProductVariantsCountFetching,
  } = useGetProductVariantsCountQuery({
    where: productVariantsWhere,
  });

  const toUseSsr =
    // 1st page took from ssr
    !!ssrProductVariants.length && currPage === 1;

  const {
    data: productVariants = [],
    isLoading: isGetProductVariantsLoading,
    isFetching: isGetProductVariantsFetching,
  } = useGetProductVariantsInfiniteQuery(
    {
      page: currPage,
      pageSize: perPage,
      order: sortBy.name !== 'Recommended' ? [sortBy.value] : undefined,
      where: productVariantsWhere,
    },
    {
      skip: toUseSsr,
      pollingInterval: PRODUCT_VARIANT_REFRESH_INTERVAL,
    }
  );

  const handleClearFilters = () => {
    setFilters([]);
    setCategories([]);
    setValueRange(ANY_VALUE_RANGE);
  };

  const productItems = productVariants.map(mapAppProductVariantToProductItem);

  const hasMore = productVariantsCount > productItems.length;

  const isLoading =
    isGetProductVariantsLoading || isGetProductVariantsCountLoading;

  const isFetching =
    isGetProductVariantsFetching || isGetProductVariantsCountFetching;

  const isQueryEmpty =
    (!!categories.length ||
      !!filters.length ||
      !isEqual(valueRange, ANY_VALUE_RANGE)) &&
    !productVariants.length;

  useEffect(() => {
    if (isFetching) return;

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore) {
        setCurrPage((p) => p + 1);
      }
    };

    observer.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    const loadMoreCur = loadMoreRef.current;
    if (loadMoreCur) {
      observer.current.observe(loadMoreCur);
    }

    return () => {
      if (observer.current && loadMoreCur) {
        observer.current.unobserve(loadMoreCur);
      }
    };
  }, [hasMore, currPage, isFetching]);

  const getFilterHandler = useCallback((fn: (v: any) => void) => {
    return (v: any) => {
      fn(v);
      setCurrPage(1);
    };
  }, []);

  return (
    <Flex
      flexDir="column"
      w="100%"
      gap={{ base: '1rem', '2xl': '1.5rem' }}
      h={isLoading ? { base: '1442px', xl: '2155px' } : 'auto'}
    >
      <Flex alignItems="center" gap="1rem">
        <Box flex="1">
          <Filters
            disabledFiltering={disabledFiltering}
            filters={filters}
            valueRange={valueRange}
            categories={categories}
            onFiltersChange={getFilterHandler(setFilters)}
            onValueRangeChange={getFilterHandler(setValueRange)}
            onCategoriesChange={getFilterHandler(setCategories)}
          />
        </Box>
        <Box flex={{ base: '1', xl: 'none' }}>
          <SortBy
            sortBy={sortBy}
            onSortByChange={(v) => {
              setSortBy(v);
              setCurrPage(1);
            }}
          />
        </Box>
      </Flex>
      {isQueryEmpty ? (
        <ProductListEmptyQueryState onClearFilters={handleClearFilters} />
      ) : (
        <>
          {isLoading ? (
            <ProductGridSkeleton />
          ) : (
            <Grid
              templateColumns={{
                base: 'repeat(2, 1fr)',
                xl: 'repeat(4, 1fr)',
              }}
              columnGap="0.5rem"
              rowGap={{ base: '0.75rem', '2xl': '1rem' }}
              w="100%"
              position="relative"
            >
              {productItems?.map((prodItem, idx) => (
                <GridItem key={`${prodItem.productVariantId}_${idx}`}>
                  <ProductItem productItem={prodItem} parentPath={parentPath} />
                </GridItem>
              ))}
            </Grid>
          )}
          <Text
            w="100%"
            textAlign="center"
            color={colors.grey[400]}
            fontSize="1.125rem"
            lineHeight="1.5rem"
          >
            {hasMore && <span ref={loadMoreRef} style={{ height: '1px' }} />}
            {isFetching ? 'Loading...' : ''}
            {!hasMore && !isFetching && !isLoading
              ? "You've reached the end!"
              : ''}
          </Text>
        </>
      )}
    </Flex>
  );
};

const ShopItemList: FC<ShopItemListProps & { withRedux?: boolean }> = ({
  withRedux = false,
  ...props
}) => {
  if (!withRedux) {
    return <ShopItemListBase {...props} />;
  }

  return (
    <ReduxProviders
      persistGateProps={{
        loading: (
          <>
            <ProductGridActionsSkeleton />
            <ProductGridSkeleton />
          </>
        ),
      }}
    >
      <ShopItemListBase {...props} />
    </ReduxProviders>
  );
};

export default ShopItemList;
